import { useMemo } from "react";
import "./FourteenthReport.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

function getSectorType(sector) {
  const s = (sector || "").toLowerCase();
  if (["agri","farm","fisheri","dairy","horticulture","rural","nabard","rkvy"].some(k => s.includes(k))) return "primary";
  if (["manufactur","electronic","ev ","electric vehicle","biotech","construction","industrial","hardware","textile","machinery","semiconductor"].some(k => s.includes(k))) return "secondary";
  return "tertiary";
}

const PHASE1 = {
  tertiary: [
    "Interview 30+ target users — focus on frequency, severity, and current workarounds for the problem",
    "Build a no-code prototype (Bubble, Glide, Webflow) and validate with 5 real users",
    "Define your ICP in 1 sentence: who, what pain, what outcome",
    "Run a concierge MVP — manually deliver the service to prove demand before building technology",
    "Map 3 direct + 3 indirect competitors; identify your top 1 differentiation point",
    "Apply to 1 incubator: T-Hub, NSRCEL, IIIT-H CIE, or Startup India registration",
  ],
  secondary: [
    "Conduct 20 buyer/operator interviews in your manufacturing target segment",
    "Build a physical proof-of-concept or 3D prototype; validate with 3 industry partners",
    "Identify regulatory requirements (BIS, ISO, MSME UDYAM) — file registrations early",
    "Apply for MSME CGTMSE loan scheme or SIDBI startup credit guarantee for pilot capital",
    "Map supply chain and identify top 3 raw material vendors with backup options",
    "Shortlist 2 pilot customers in manufacturing/industrial sector for beta testing",
  ],
  primary: [
    "Map the specific agri/rural problem with district-level data from govt portals (NABARD, AgriStack)",
    "Connect with FPO or cooperative society to validate solution with 20+ farmers",
    "Apply for RKVY-RAFTAAR, NABARD NRETP, or DST NM-ICPS grant for early funding",
    "Build a WhatsApp or IVR-based MVP to test without internet dependency",
    "Identify a govt buyer, NAFED, or state agri board as anchor customer for pilot",
    "Map seasonal cycle — align MVP launch with the crop/harvest calendar",
  ],
};

function getPhase2(bm, revModel, unfairAdv, maturity) {
  const bmL   = (bm        || "").toLowerCase();
  const revL  = (revModel  || "").toLowerCase();
  const unfL  = (unfairAdv || "").replace(/[^A-Za-z0-9 ]/g,"").toLowerCase();
  const matL  = (maturity  || "").toLowerCase();

  const tasks = [
    bmL.includes("b2b")
      ? "Sign 3 LOIs (Letters of Intent) from target companies before building the full product"
      : "Launch a waitlist landing page; target 500 signups as PMF signal before full launch",
    revL.includes("subscription")
      ? "Set up billing infrastructure (Razorpay/Stripe) and activate a 14-day free trial funnel"
      : "Build a pilot pricing model; test 3 price points with 10 customers each",
    "Build core MVP: 1 core user flow that solves the #1 pain — nothing more",
    "Measure Week 2 retention as your primary PMF metric; iterate weekly based on data",
    unfL.includes("no") || unfL === ""
      ? "Identify your moat strategy: proprietary data, network effects, exclusive partnerships, or speed"
      : "Strengthen your unfair advantage: protect IP, file provisional patent, or lock exclusive data",
    matL.includes("poc") || matL.includes("mvp")
      ? "Onboard 5 beta users; run structured feedback sessions weekly for 4 weeks"
      : "Iterate on retention — focus on activation, engagement, and core use case depth",
  ];
  return tasks;
}

function getPhase3(bm, channel, investorFit, sector) {
  const bmL  = (bm          || "").toLowerCase();
  const chL  = (channel     || "").toLowerCase();
  const invL = (investorFit || "").toLowerCase();
  const secL = (sector      || "").toLowerCase();

  const tasks = [
    chL.includes("social") || chL.includes("content")
      ? "Launch organic content strategy: 3 posts/week on LinkedIn + 1 case study/month"
      : "Activate 1 channel aggressively: partnerships, communities, or direct outreach (not paid ads yet)",
    bmL.includes("b2c")
      ? "Launch referral programme: give 1 free month for every 2 paying referrals"
      : "Close first 3 paying enterprise pilots at discounted 'design partner' pricing",
    "Build key metrics dashboard: CAC, LTV, churn, MRR, NPS — visible to entire team",
    invL.includes("impact") || invL.includes("stage")
      ? "Prepare Seed pitch deck: problem, solution, traction, team, market size, ask"
      : "Document unit economics clearly: COGS, gross margin, CAC, payback period",
    secL.includes("agri") || secL.includes("rural")
      ? "Pitch to impact investors: Ankur Capital, Acumen India, Omnivore Partners, Villgro"
      : bmL.includes("b2b")
        ? "Apply to YC India, Axilor, or 100X.VC with 3 months of revenue data as proof"
        : "Apply to consumer-focused VCs: Blume, Kalaari, Stellaris, or Matrix India",
    "Schedule 10 investor calls in Month 3; use warm intros through accelerator network",
  ];
  return tasks;
}

const FourteenthReport = ({ res }) => {
  const sector      = res?.sector           ?? "your sector";
  const bm          = res?.businessModel    ?? "";
  const revModel    = res?.revenueStreams    ?? "";
  const unfairAdv   = res?.unfairAdvantage  ?? "";
  const maturity    = res?.productMaturity  ?? "";
  const channel     = res?.bestFitChannel   ?? "";
  const investorFit = res?.investorFit      ?? "";

  const sectorType = useMemo(() => getSectorType(sector), [sector]);
  const phase1Tasks = PHASE1[sectorType];
  const phase2Tasks = useMemo(() => getPhase2(bm, revModel, unfairAdv, maturity), [bm, revModel, unfairAdv, maturity]);
  const phase3Tasks = useMemo(() => getPhase3(bm, channel, investorFit, sector),  [bm, channel, investorFit, sector]);

  const phases = [
    {
      num: 1, label: "Validate", days: "Day 1–30",
      theme: "#2563eb", accent: "#dbeafe", text: "#1e3a8a",
      icon: "🔍",
      subtitle: "Discover · Empathise · Prototype",
      tasks: phase1Tasks,
    },
    {
      num: 2, label: "Build MVP", days: "Day 31–60",
      theme: "#7c3aed", accent: "#ede9fe", text: "#4c1d95",
      icon: "🛠️",
      subtitle: "Build · Test · Iterate",
      tasks: phase2Tasks,
    },
    {
      num: 3, label: "Launch", days: "Day 61–90",
      theme: "#059669", accent: "#d1fae5", text: "#064e3b",
      icon: "🚀",
      subtitle: "Launch · Acquire · Fundraise",
      tasks: phase3Tasks,
    },
  ];

  return (
    <div className="iv-rb-fourteenth-container">
      <div className="iv-rb-fourteenth-final-table">
        <span className="iv-rb-fourteenth-corner iv-rb-fourteenth-corner-top-left" />
        <span className="iv-rb-fourteenth-corner iv-rb-fourteenth-corner-top-right" />
        <span className="iv-rb-fourteenth-corner iv-rb-fourteenth-corner-bottom-left" />
        <span className="iv-rb-fourteenth-corner iv-rb-fourteenth-corner-bottom-right" />

        <div className="iv-rb-fourteenth-module-title">
          <span className="iv-rb-fourteenth-mod-prefix">Module 13:</span> 90-Day Idea Validation Roadmap
          <span className="iv-rb-fourteenth-premium-tag">★ Report A+ Exclusive</span>
        </div>

        <QTag
          q="What sector do you operate in, what is your business model, marketing channel, and investor type preference?"
          a={`Sector: ${sector} · Model: ${bm || "—"} · Channel: ${channel || "—"} · Investor fit: ${investorFit || "—"}`}
          impacts="Phase 1 validation strategy, Phase 2 MVP approach, Phase 3 launch and fundraising path"
        />

        <div className="iv-rb-fourteenth-context-bar">
          Sector: <strong>{sector}</strong> · Type: <strong>{sectorType}</strong> · Model: <strong>{bm || "—"}</strong> · Revenue: <strong>{revModel || "—"}</strong> · Stage: <strong>{maturity || "—"}</strong>
        </div>

        <div className="iv-rb-fourteenth-phases-grid">
          {phases.map((ph) => (
            <div key={ph.num} className="iv-rb-fourteenth-phase-card" style={{borderTop:`4px solid ${ph.theme}`}}>
              <div className="iv-rb-fourteenth-phase-header" style={{background: ph.accent}}>
                <span className="iv-rb-fourteenth-phase-icon">{ph.icon}</span>
                <div>
                  <div className="iv-rb-fourteenth-phase-label" style={{color: ph.theme}}>
                    Phase {ph.num}: {ph.label}
                  </div>
                  <div className="iv-rb-fourteenth-phase-days" style={{color: ph.text}}>{ph.days}</div>
                  <div className="iv-rb-fourteenth-phase-subtitle" style={{color: ph.text}}>{ph.subtitle}</div>
                </div>
              </div>
              <ul className="iv-rb-fourteenth-task-list">
                {ph.tasks.map((task, i) => (
                  <li key={i} className="iv-rb-fourteenth-task-item">
                    <span className="iv-rb-fourteenth-task-dot" style={{background: ph.theme}} />
                    <span>{task}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="iv-rb-fourteenth-footer">
          © MarsMate AI · 90-day roadmap is personalised to your sector ({sector}), business model ({bm || "—"}), and product stage ({maturity || "—"}). Revisit and update every 30 days based on traction signals.
        </div>
      </div>
    </div>
  );
};

export default FourteenthReport;
